<script setup lang="ts">
  import { useModal } from '@/composition/use-modal';
  import { useOrdering } from '@/composition/use-ordering';
  import { useOrders } from '@/composition/use-orders';
  import { useUi } from '@/composition/use-ui';
  import { OrderType } from '@/enums/OrderEnums';
  import { AlertType } from '@/interfaces/IAlert';
  import { useRestaurantStore } from '@/stores/restaurant.store';
  import { displayPrice } from '@/utils';
  import { breakpointsTailwind, useBreakpoints } from '@vueuse/core';
  import { inject, computed, ref } from 'vue';
  import { useRouter } from 'vue-router/composables';

  const modals = useModal();
  const ui = useUi();
  const restaurantStore = useRestaurantStore();
  const orderHelper = inject<ReturnType<typeof useOrdering>>('orderHelper');
  const orders = useOrders();
  const breakpoints = useBreakpoints(breakpointsTailwind);

  const isSmallDevice = breakpoints.smaller('sm');

  const cart = computed(() => orderHelper.cart.value);

  const saving = ref(false);
  async function finish() {
    if (saving.value) return;

    try {
      saving.value = true;
      const savedOrder = await orderHelper.createOrder();
      modals.hide('pos-new-order', savedOrder);

      if (orderHelper.editOrder.value) {
        ui.showToast(AlertType.Success, 'Pedido actualizado');
      } else {
        if (savedOrder.type === OrderType.Seat) {
          ui.showToast(AlertType.Success, 'Pedido creado', {
            text: 'Ver pedido',
            cb: () => orders.openOrder(savedOrder.id),
          });
        } else {
          orders.openOrder(savedOrder.id);
        }
      }
    } catch (error) {
      ui.serverError(error, 'Error al finalizar el pedido');
      saving.value = false;
    }
  }

  function cancel() {
    modals.hide('pos-new-order');
  }
</script>

<template>
  <div class="cart-footer">
    <div class="details">
      <span class="quantity">
        {{ orderHelper.currentCartTotalItems.value }} productos
      </span>
      <span class="total">
        {{ restaurantStore.currency?.symbol
        }}{{ displayPrice(orderHelper.currentCartTotal.value) }}
      </span>
    </div>
    <div class="actions">
      <v-btn color="gray" rounded @click="cancel" depressed :disabled="saving">
        <v-icon left>mdi-close</v-icon>
        Cancelar
      </v-btn>
      <v-btn
        color="primary"
        rounded
        :disabled="orderHelper.currentCartTotalItems.value === 0"
        :loading="saving"
        @click="finish"
      >
        <v-icon left>mdi-cart-arrow-right</v-icon>
        {{ !isSmallDevice ? 'Finalizar pedido' : 'Finalizar' }}
      </v-btn>
    </div>
  </div>
</template>

<style scoped lang="scss">
  .cart-footer {
    @apply tw-flex tw-items-center tw-justify-between tw-px-0 sm:tw-px-4 tw-py-2;

    .details {
      @apply tw-flex tw-flex-col tw-items-start tw-justify-center;

      .quantity {
        @apply tw-text-xs;
      }

      .total {
        @apply tw-font-bold tw-text-xl;
      }
    }

    .actions {
      @apply tw-flex-none tw-flex tw-items-center tw-space-x-2;
    }
  }
</style>
